import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { AuthProvider } from '@/auth/AuthProvider'
import { AuthError, dashboardMutationDefaults } from '@/api/client'
import { isTokenAuthMode, stagingTokenStorageKey, stagingTokenUserStorageKey } from '@/auth/auth-mode'
import { createAuthenticationRecovery } from '@/auth/auth-recovery'
import { RootErrorBoundary } from '@/components/root-error-boundary'

import { routeTree } from './routeTree.gen'

import './index.css'
import { registerAlertsWorker } from '@/features/storefront-chat/phone-alerts-device'
import { installPracticeFetch } from '@/features/learn/practice/engine'
import { initAnalytics } from '@/lib/analytics'
import { createSaveOrderGuard } from '@/api/save-order-guard'

initAnalytics()

// Practice mode answers API calls itself while a practice session is open (USA-1544).
installPracticeFetch()

async function recoverMsalSession() {
  const [instanceModule, configModule, tokenModule, browserModule, persistenceModule] =
    await Promise.all([
      import('@/auth/msalInstance'),
      import('@/auth/msalConfig'),
      import('@/auth/acquireToken'),
      import('@azure/msal-browser'),
      import('@/auth/session-persistence'),
    ])
  // A top-level redirect is the only silent renewal browsers still allow once
  // the 24h SPA refresh token expires; the hint lets Microsoft finish it from
  // its own session and the saved path brings the user back where they were.
  const redirectToSignIn = () => {
    persistenceModule.saveReturnPath(
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    )
    return instanceModule.msalInstance.loginRedirect(configModule.signInRequest())
  }
  const account = instanceModule.msalInstance.getActiveAccount()
  if (!account) {
    await redirectToSignIn()
    return
  }
  try {
    await tokenModule.acquireTokenWithRetry(instanceModule.msalInstance, account, {
      ...configModule.loginRequest,
      forceRefresh: true,
    })
    void queryClient.invalidateQueries()
  } catch (refreshError) {
    if (refreshError instanceof browserModule.InteractionRequiredAuthError) {
      await redirectToSignIn()
      return
    }
    throw refreshError
  }
}

const recoverAuthentication = createAuthenticationRecovery({
  mode: isTokenAuthMode ? 'token' : 'msal',
  onTokenUnauthorized: () => {
    try {
      window.localStorage.removeItem(stagingTokenStorageKey)
      window.localStorage.removeItem(stagingTokenUserStorageKey)
    } catch {
      // Ignore locked-down storage.
    }
    window.location.assign('/login')
  },
  onMsalUnauthorized: recoverMsalSession,
  onRecoveryError: (refreshError) => {
    console.error('MSAL session recovery failed:', refreshError)
  },
})

const saveOrder = createSaveOrderGuard()
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: recoverAuthentication,
  }),
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => saveOrder.stored(mutation),
    onError: (error, _variables, _context, mutation) => {
      saveOrder.stored(mutation)
      return recoverAuthentication(error)
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30,
      retry: (failureCount, error) => {
        if (error instanceof AuthError) return false
        return failureCount < 3
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
    },
    mutations: dashboardMutationDefaults,
  },
})

saveOrder.install(queryClient)

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={null}>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </Suspense>
      </QueryClientProvider>
    </RootErrorBoundary>
  </StrictMode>,
)

registerAlertsWorker()
